import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export interface UndoRedoAction {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

const DEFAULT_HISTORY_LIMIT = 50;
const canAlwaysApply = () => true;

function isEditableTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT" ||
      target.isContentEditable)
  );
}

export function useUndoRedoHistory(
  scopeKey: string,
  limit = DEFAULT_HISTORY_LIMIT,
  onError: (error: unknown) => void = console.error,
  enabled = true,
  canApply: () => boolean = canAlwaysApply,
) {
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new Error("Undo/redo history limit must be a positive integer");
  }
  const undoStackRef = useRef<UndoRedoAction[]>([]);
  const redoStackRef = useRef<UndoRedoAction[]>([]);
  const pendingRecordsRef = useRef<Array<{ action: UndoRedoAction; generation: number }>>([]);
  const busyRef = useRef(false);
  const activeApplicationsRef = useRef(new Map<string, object>());
  const generationRef = useRef(0);
  const scopeKeyRef = useRef(scopeKey);
  const scopeToken = useMemo(() => ({}), [scopeKey]);
  const scopeTokenRef = useRef(scopeToken);
  const onErrorRef = useRef(onError);
  const [counts, setCounts] = useState({ undo: 0, redo: 0 });
  const [isBusy, setIsBusy] = useState(false);

  onErrorRef.current = onError;

  const syncCounts = useCallback(() => {
    setCounts({
      undo: undoStackRef.current.length,
      redo: redoStackRef.current.length,
    });
  }, []);

  const clear = useCallback(() => {
    generationRef.current += 1;
    busyRef.current = activeApplicationsRef.current.has(scopeKeyRef.current);
    undoStackRef.current = [];
    redoStackRef.current = [];
    pendingRecordsRef.current = [];
    setIsBusy(busyRef.current);
    syncCounts();
  }, [syncCounts]);

  useLayoutEffect(() => {
    scopeTokenRef.current = scopeToken;
    if (scopeKeyRef.current !== scopeKey) {
      scopeKeyRef.current = scopeKey;
      generationRef.current += 1;
      busyRef.current = activeApplicationsRef.current.has(scopeKey);
      undoStackRef.current = [];
      redoStackRef.current = [];
      pendingRecordsRef.current = [];
      setIsBusy(busyRef.current);
    }
    syncCounts();
  }, [scopeKey, scopeToken, syncCounts]);

  const record = useCallback(
    (action: UndoRedoAction) => {
      if (scopeTokenRef.current !== scopeToken) return;
      if (busyRef.current) {
        pendingRecordsRef.current.push({ action, generation: generationRef.current });
        return;
      }
      undoStackRef.current = [...undoStackRef.current, action].slice(-limit);
      redoStackRef.current = [];
      syncCounts();
    },
    [limit, scopeToken, syncCounts]
  );

  const apply = useCallback(
    async (direction: "undo" | "redo") => {
      if (scopeTokenRef.current !== scopeToken || busyRef.current || !canApply()) return false;
      const source = direction === "undo" ? undoStackRef.current : redoStackRef.current;
      const action = source[source.length - 1];
      if (!action) return false;
      const generation = generationRef.current;
      const application = {};

      busyRef.current = true;
      activeApplicationsRef.current.set(scopeKey, application);
      setIsBusy(true);
      try {
        await action[direction]();
        if (generationRef.current !== generation || scopeKeyRef.current !== scopeKey) {
          return false;
        }
        if (direction === "undo") {
          undoStackRef.current = undoStackRef.current.slice(0, -1);
          redoStackRef.current = [...redoStackRef.current, action];
        } else {
          redoStackRef.current = redoStackRef.current.slice(0, -1);
          undoStackRef.current = [...undoStackRef.current, action];
        }
        syncCounts();
        return true;
      } finally {
        if (activeApplicationsRef.current.get(scopeKey) === application) activeApplicationsRef.current.delete(scopeKey);
        if (generationRef.current === generation && scopeKeyRef.current === scopeKey) {
          busyRef.current = false;
          setIsBusy(false);
          const pending = pendingRecordsRef.current.filter(
            (entry) => entry.generation === generation
          );
          pendingRecordsRef.current = [];
          if (pending.length > 0) {
            undoStackRef.current = [
              ...undoStackRef.current,
              ...pending.map((entry) => entry.action),
            ].slice(-limit);
            redoStackRef.current = [];
            syncCounts();
          }
        } else if (scopeKeyRef.current === scopeKey) {
          busyRef.current = activeApplicationsRef.current.has(scopeKey);
          setIsBusy(busyRef.current);
          if (!busyRef.current) {
            const pending = pendingRecordsRef.current.filter((entry) => entry.generation === generationRef.current);
            pendingRecordsRef.current = [];
            if (pending.length > 0) {
              undoStackRef.current = [...undoStackRef.current, ...pending.map((entry) => entry.action)].slice(-limit);
              redoStackRef.current = [];
              syncCounts();
            }
          }
        }
      }
    },
    [canApply, limit, scopeKey, scopeToken, syncCounts]
  );

  const undo = useCallback(() => apply("undo"), [apply]);
  const redo = useCallback(() => apply("redo"), [apply]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target) || !(event.metaKey || event.ctrlKey)) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey && undoStackRef.current.length > 0 && !busyRef.current) {
        event.preventDefault();
        void undo().catch(onErrorRef.current);
      } else if (
        ((key === "z" && event.shiftKey) || (key === "y" && event.ctrlKey)) &&
        redoStackRef.current.length > 0 &&
        !busyRef.current
      ) {
        event.preventDefault();
        void redo().catch(onErrorRef.current);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, redo, undo]);

  return {
    record,
    clear,
    undo,
    redo,
    canUndo: counts.undo > 0,
    canRedo: counts.redo > 0,
    undoCount: counts.undo,
    redoCount: counts.redo,
    isBusy,
    isApplying: () => activeApplicationsRef.current.has(scopeKeyRef.current),
  };
}
